import { useCallback, useContext, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';

import { DataSourceProvider as ReferentialityDataSourceProvider } from '@atlassian/editor-referentiality';

import type { ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent';
import { getConnectDataListener } from '@confluence/app-referentiality/entry-points/connectDataListener';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext';
import { getSsrExtensionHandlers } from '@confluence/content-renderer-legacy-macros/entry-points/getSsrExtensionHandlers';
import { removeDuplicateResourcesForMacros } from '@confluence/content-renderer-legacy-macros/entry-points/ssrTransformers';
import type { ContentUnifiedQueryMacroRenderedOutputType } from '@confluence/content-unified-query';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { includeSuperbatchForFabricMacro } from '../PageContentRenderer';

import { createExtensionHandlersWithoutSSR } from './createExtensionHandlersWithoutSSR';
import type { PageContentRendererFabricProps } from './PageContentRendererFabricProps';

export function useExtensionHandlersForPageContentRendererFabric(
	{
		content,
		contentId,
		contentBody,
		isTemplateEditorRenderer,
		spaceKey,
		spaceId,
		isPreviewMode,
	}: PageContentRendererFabricProps,
	annotationProvider: ADFRendererProps['annotationProvider'],
) {
	const { featureFlags, environment, cloudId, userId, locale, activationId } = useSessionData();
	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [manifestReadyState, setManifestReadyState] = useState(false);
	const { getExtensionHandlers, populateExtensionProviders } = useContext(RendererExtensionContext);

	const contentType = content?.type;
	const adf = contentBody?.value || '';

	const macroRenderedOutput = useMemo(
		() =>
			content && 'macroRenderedOutput' in content
				? removeDuplicateResourcesForMacros(
						content['macroRenderedOutput'] as ContentUnifiedQueryMacroRenderedOutputType[],
					)
				: null,
		[content],
	);

	// If legacy fabric macros exist, superbatch css and js needs to be added
	// only once before any macros are rendered.
	const includeSuperbatch = useCallback(() => {
		includeSuperbatchForFabricMacro(contentBody, macroRenderedOutput);
	}, [contentBody, macroRenderedOutput]);

	// Extension handlers during SSR should not be used to populate extension providers
	// Otherwise extensions manifest would reference the wrong handlers if it needs to be overrided
	// during runtime
	const extensionHandlersWithSSR = useMemo(
		() =>
			getSsrExtensionHandlers({
				adf: contentBody?.value,
				contentId: contentId || '',
				spaceKey,
				cloudId,
				userId,
				macroRenderedOutput,
				includeSuperbatch,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[includeSuperbatch],
	);

	const referentialityDataSourceProvider = useMemo(() => {
		const refDataProvider = new ReferentialityDataSourceProvider();

		getConnectDataListener().init({
			emit: (localId, reference) => refDataProvider.createOrUpdate(localId, reference),
		});

		return refDataProvider;
	}, []);

	const extensionHandlers = useMemo(() => {
		return getExtensionHandlers?.(
			{
				adf,
				contentId,
				spaceKey,
				macroRenderedOutput,
				showTemplateVariablesInPreview: isTemplateEditorRenderer,
				shouldIncludeMacroWithReferencesHandlers: true,
				referentialityDataSourceProvider,
				contentType,
				isPreviewMode,
				annotationProvider,
			},
			createAnalyticsEvent,
		) as ADFRendererProps['extensionHandlers'];
	}, [
		getExtensionHandlers,
		contentId,
		spaceKey,
		adf,
		macroRenderedOutput,
		contentType,
		isPreviewMode,
		createAnalyticsEvent,
		isTemplateEditorRenderer,
		annotationProvider,
		referentialityDataSourceProvider,
	]);

	const isSsrRendered = window.__SSR_RENDERED__;
	// The extensionHandlers prop value of PageContentRendererFabric when NOT isTemplateEditorRenderer:
	const handlers: ADFRendererProps['extensionHandlers'] = useMemo(() => {
		if (!isTemplateEditorRenderer) {
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR) {
				// Return extension handlers for supported content only
				return extensionHandlersWithSSR;
			} else if (!manifestReadyState) {
				// POST/NON SSR, macros V2 is on, and manifest is ready, extension handlers is undefined to use extension manifest
				// Don't use client side macro renderer until manifestReadyState because it takes additional cycle to render
				// The loading state will destroy the SSR rendered content
				//
				// The first render of client-side need extensionHandlersWithSSR for proper hydration as well.
				if (isSsrRendered) {
					return extensionHandlersWithSSR;
				} else {
					return createExtensionHandlersWithoutSSR(extensionHandlers);
				}
			}
		}
	}, [
		extensionHandlers,
		extensionHandlersWithSSR,
		isSsrRendered,
		isTemplateEditorRenderer,
		manifestReadyState,
	]);

	const dataProviders: ADFRendererProps['dataProviders'] = useMemo(() => {
		const providerFactory = ProviderFactory.create({});
		const intlStub = {
			formatMessage: (message) => message?.defaultMessage,
			locale,
		};

		if (populateExtensionProviders) {
			populateExtensionProviders(providerFactory, {
				intl: intlStub,
				containerId: spaceKey,
				accountId: userId,
				cloudId,
				spaceKey,
				spaceId,
				contentId,
				contentType,
				environment,
				featureFlags,
				extensionHandlers,
				setManifestReadyState,
				createAnalyticsEvent,
				activationId,
				isLivePagesFeatureEnabled,
			});
		}

		return providerFactory;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		contentId,
		contentType,
		extensionHandlers,
		populateExtensionProviders,
		isLivePagesFeatureEnabled,
		createAnalyticsEvent,
	]);

	return {
		dataProviders,
		extensionHandlers: isTemplateEditorRenderer ? extensionHandlers : handlers,
	};
}
